// Phase 15 Plan 08 Task 3 — Leaderboard screen.
//
// Renders:
//   1. LadderSwitcher (3 modes: Standard 4L / Open 3L / Purist; locale-paired
//      via $locale → full LadderKey)
//   2. Top-100 list (when slice_kind === 'top100')
//   3. RankRegionPanel + near-slice rows (when slice_kind === 'near')
//   4. Sticky "Your rank" row at bottom (when caller is sub-top-100, OR
//      caller is in top 100 with the in-list highlight instead)
//   5. Empty state when entries.length === 0
//
// Data flow (Plan 04 + Plan 05):
//   - First fetch:  fetchLeaderboard(ladderKey)         → top-100 cache key
//   - If caller NOT in top 100 (your_rank > 100 OR undefined in top100):
//     second fetch with { near: true } → returns slice_kind='near' +
//     rank_region payload (server projects percentile bands — DEC-021).
//   - Cached in $leaderboardCache (60s TTL, mirrors server KV).
//
// Pitfall 5 (T-15-37) — `?near=true` is a BOOLEAN flag, never user_uuid.
// The handler reads identity from the bearer ONLY. The Plan-05 net wrapper
// already encodes this verbatim; we just toggle the flag.

import { useStore } from '@nanostores/react'
import { type JSX, useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useDocumentTitle } from '../../i18n/useDocumentTitle'
import {
  fetchLeaderboard,
  type LadderKey,
  type LeaderboardResponse,
} from '../../net/leaderboard'
import { useRouter } from '../../router/router'
import { $authBearer } from '../../stores/auth'
import {
  getCachedLeaderboard,
  setCachedLeaderboard,
} from '../../stores/leaderboardCache'
import { $locale } from '../../stores/locale'
import { Screen, ScreenHeader } from '../components/Screen.js'
import {
  buildLadderKey,
  type LadderMode,
  LadderSwitcher,
} from './LadderSwitcher'
import { LeaderboardRow } from './LeaderboardRow'
import { RankRegionPanel } from './RankRegionPanel'

type LoadKind =
  | { kind: 'loading' }
  | { kind: 'loaded'; response: LeaderboardResponse }
  | { kind: 'empty' }
  | { kind: 'error' }

export function LeaderboardScreen(): JSX.Element {
  useDocumentTitle('doc.title.leaderboard')
  const intl = useIntl()
  const router = useRouter()
  const locale = useStore($locale)
  const bearer = useStore($authBearer)
  const selfUuid = bearer?.user_uuid ?? null

  const [selectedMode, setSelectedMode] = useState<LadderMode>('standard_4')
  const [state, setState] = useState<LoadKind>({ kind: 'loading' })
  // Holds the response from the `?near=true` second fetch when applicable.
  // When `state.response.slice_kind === 'near'` already, we never make the
  // second fetch — the first response is sufficient. The variable below is
  // for the corner case where the top-100 fetch returned slice_kind='top100'
  // but the caller's rank is > 100 → we fire a second fetch to populate the
  // rank-region.
  const [nearResponse, setNearResponse] = useState<LeaderboardResponse | null>(
    null,
  )

  const ladderKey: LadderKey = buildLadderKey(selectedMode, locale)

  // Top-level fetch (cached). Re-fires on ladder change. Also resets the
  // near-response slot so the rank-region from a previous ladder cannot
  // bleed onto a different ladder's view.
  useEffect(() => {
    setNearResponse(null)
    const cached = getCachedLeaderboard(ladderKey)
    if (cached) {
      setState({ kind: 'loaded', response: cached })
      return
    }
    let cancelled = false
    setState({ kind: 'loading' })
    void fetchLeaderboard(ladderKey)
      .then((data) => {
        if (cancelled) return
        setCachedLeaderboard(ladderKey, data)
        if (data.entries.length === 0) {
          setState({ kind: 'empty' })
        } else {
          setState({ kind: 'loaded', response: data })
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // 4xx/5xx — both render the generic empty banner. apiFetch already
        // retried 5xx + handled 401 by clearing auth.
        void err
        setState({ kind: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [ladderKey])

  // Conditional near-fetch: if the top-100 response indicates the caller is
  // NOT in the top 100 (or we have no rank context yet), pull the near-slice
  // so we can render the rank-region panel + sticky row.
  useEffect(() => {
    if (state.kind !== 'loaded') return
    if (state.response.slice_kind === 'near') return
    if (!selfUuid) return
    // For top100 responses we can detect "caller is in top 100" by scanning
    // entries. If absent, the caller is sub-top-100 → fetch the near slice.
    const inTop100 = state.response.entries.some(
      (e) => e.user_uuid === selfUuid,
    )
    if (inTop100) return

    let cancelled = false
    void fetchLeaderboard(ladderKey, { near: true })
      .then((data) => {
        if (cancelled) return
        setNearResponse(data)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        void err
        // Swallow — the top-100 list is still visible; we just can't show
        // the rank-region panel. No need to surface an error banner.
      })
    return () => {
      cancelled = true
    }
  }, [state, selfUuid, ladderKey])

  const handleSelectMode = useCallback((mode: LadderMode) => {
    setSelectedMode(mode)
  }, [])

  return (
    <Screen
      name="leaderboard"
      gutter={16}
      contentStyle={{ gap: 'var(--space-5)', alignItems: 'center' }}
      mainProps={{ 'data-ladder-key': ladderKey }}
      header={
        <ScreenHeader
          title={
            <FormattedMessage
              id="social.leaderboard.title"
              defaultMessage="Leaderboard"
            />
          }
          onBack={() => router.navigate('/lobby')}
          backLabel={intl.formatMessage({
            id: 'nav.back',
            defaultMessage: 'Back',
          })}
        />
      }
    >
      <div style={{ width: '100%' }}>
        <LadderSwitcher
          selectedMode={selectedMode}
          onSelect={handleSelectMode}
        />
      </div>

      {state.kind === 'loading' && (
        <p
          role="status"
          aria-live="polite"
          style={{
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <FormattedMessage
            id="social.leaderboard.loading"
            defaultMessage="Loading rankings…"
          />
        </p>
      )}

      {state.kind === 'empty' && (
        <section
          aria-label={intl.formatMessage({
            id: 'social.leaderboard.empty_section_aria',
          })}
          data-state="empty"
          style={{
            width: '100%',
            padding: 'var(--space-5) var(--space-4)',
            backgroundColor: 'var(--color-card)',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-card)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            textAlign: 'center',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-18)',
            }}
          >
            <FormattedMessage
              id="social.leaderboard.empty.title"
              defaultMessage="No rankings yet"
            />
          </h2>
          <p
            style={{
              margin: 0,
              fontFamily: 'var(--font-family-body)',
              fontWeight: 500,
              fontSize: 'var(--font-size-14)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <FormattedMessage
              id="social.leaderboard.empty.body"
              defaultMessage="Play a ranked match to get on the board."
            />
          </p>
        </section>
      )}

      {state.kind === 'error' && (
        <p
          role="alert"
          style={{
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-primary)',
            textAlign: 'center',
            maxWidth: '320px',
          }}
        >
          <FormattedMessage
            id="social.error.leaderboard_failed"
            defaultMessage="The board's resting. Try again soon."
          />
        </p>
      )}

      {state.kind === 'loaded' && (
        <LeaderboardBody
          response={state.response}
          nearResponse={nearResponse}
          selfUuid={selfUuid}
        />
      )}
    </Screen>
  )
}

interface LeaderboardBodyProps {
  readonly response: LeaderboardResponse
  readonly nearResponse: LeaderboardResponse | null
  readonly selfUuid: string | null
}

function LeaderboardBody(props: LeaderboardBodyProps): JSX.Element {
  const { response, nearResponse, selfUuid } = props
  const intl = useIntl()

  // Resolve which response to render entries from:
  //   - If the first response is already a near-slice (server may collapse
  //     `?near=true` when caller is in top-100), use it directly.
  //   - Otherwise use top100 entries + (optional) nearResponse for the
  //     rank-region + extended near-slice rendering.
  const isNearPrimary = response.slice_kind === 'near'
  const sliceForRegion = isNearPrimary ? response : nearResponse
  const rankRegion = sliceForRegion?.rank_region ?? null
  // CR-06: only consider the caller "in top 100" when the primary response
  // IS the top-100 slice. When slice_kind === 'near', `response.entries` is
  // the ±10-rank window around the caller by construction — it always
  // contains the caller's UUID — and the previous check wrongly suppressed
  // the sticky "Your rank" row for callers outside the top 100.
  const callerInTop100 =
    selfUuid !== null &&
    response.slice_kind === 'top100' &&
    response.entries.some((e) => e.user_uuid === selfUuid)

  return (
    <section
      aria-label={intl.formatMessage({
        id: 'social.leaderboard.top_players_aria',
      })}
      data-section="leaderboard-entries"
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
      }}
    >
      {/* Rank-region panel renders ONLY when slice_kind==='near' AND a
          rank_region is present (caller is sub-top-100). */}
      {rankRegion && <RankRegionPanel rankRegion={rankRegion} />}

      {response.entries.map((entry, index) => {
        const rank = index + 1
        const isSelf = entry.user_uuid === selfUuid
        return (
          <LeaderboardRow
            key={`${entry.user_uuid}-${rank}`}
            rank={rank}
            userUuid={entry.user_uuid}
            displayName={entry.display_name}
            mu={entry.mu}
            isSelf={isSelf}
          />
        )
      })}

      {/* Sticky bottom "Your rank" row — only when caller is NOT in the
          top 100 AND we have a rank_region from the near-slice. The
          in-top-100 case relies on the inline isSelf highlight above. */}
      {!callerInTop100 && rankRegion && selfUuid && (
        <LeaderboardRow
          rank={rankRegion.your_rank}
          userUuid={selfUuid}
          displayName={resolveSelfDisplayName(sliceForRegion, selfUuid)}
          mu={resolveSelfMu(sliceForRegion, selfUuid)}
          isSelf={true}
          sticky={true}
        />
      )}
    </section>
  )
}

/**
 * Find the caller's display_name in the near-slice (server-projected). If
 * the near-slice is missing or the caller's row is absent, fall back to a
 * blank — UI never invents a name.
 */
function resolveSelfDisplayName(
  sliceForRegion: LeaderboardResponse | null,
  selfUuid: string,
): string {
  if (!sliceForRegion) return ''
  const row = sliceForRegion.entries.find((e) => e.user_uuid === selfUuid)
  return row?.display_name ?? ''
}

function resolveSelfMu(
  sliceForRegion: LeaderboardResponse | null,
  selfUuid: string,
): number {
  if (!sliceForRegion) return 0
  const row = sliceForRegion.entries.find((e) => e.user_uuid === selfUuid)
  return row?.mu ?? 0
}
